import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import HeadingComponent from "@/components/BoardPack/components/shared/heading";
import DonutChart from "@/components/BoardPack/components/charts/DonutChart";

const STATIC_RESPONSE = {
  success: true,
  meta: { contract: "0020016", years: [2023, 2024, 2025], kpi: "occupancy" },
  data: {
    "0020016 - JEDDAH ARPT MGT CONTRACT": {
      Parking_Occupancy: {
        2023: {
          Jan: "11%", Feb: "10%", Mar: "11%", Apr: "9%", May: "9%", Jun: "9%",
          Jul: "9%", Aug: "11%", Sep: "10%", Oct: "10%", Nov: "10%", Dec: "11%",
          Full_Year: "10%",
        },
        isPercent: true,
        2024: {
          Jan: "11%", Feb: "11%", Mar: "10%", Apr: "9%", May: "10%", Jun: "8%",
          Jul: "10%", Aug: "10%", Sep: "9%", Oct: "9%", Nov: "11%", Dec: "10%",
          Full_Year: "10%",
        },
        2025: {
          Jan: "10%", Feb: "11%", Mar: "8%", Apr: "9%", May: "9%", Jun: "8%",
          Jul: "10%", Aug: "10%", Sep: "9%", Oct: "9%", Nov: "10%", Dec: "10%",
          Full_Year: "10%",
        },
      },
    },
  },
};

const Kpi_Chart_13 = ({ year = "", month = "", monthNumber = "", contract = "", page = null }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "null") return "0%";
    return value;
  };

  const processData = () => {
    try {
      setError(null);
      setLoading(true);

      const response = STATIC_RESPONSE;

      if (!response?.success) {
        throw new Error(response?.message || "Response unsuccessful");
      }

      if (response?.data) {
        const transformedData = [];
        const contractKey = Object.keys(response.data)[0];
        const contractData = response.data[contractKey];
        const kpiKey = Object.keys(contractData)[0];
        const kpiData = contractData[kpiKey];
        const years = Object.keys(kpiData).filter((key) => key !== "isPercent");

        years.forEach((yr) => {
          const yearData = kpiData[yr];
          const months = [
            { m: "Jan", v: formatValue(yearData.Jan) },
            { m: "Feb", v: formatValue(yearData.Feb) },
            { m: "Mar", v: formatValue(yearData.Mar) },
            { m: "Apr", v: formatValue(yearData.Apr) },
            { m: "May", v: formatValue(yearData.May) },
            { m: "Jun", v: formatValue(yearData.Jun) },
            { m: "Jul", v: formatValue(yearData.Jul) },
            { m: "Aug", v: formatValue(yearData.Aug) },
            { m: "Sep", v: formatValue(yearData.Sep) },
            { m: "Oct", v: formatValue(yearData.Oct) },
            { m: "Nov", v: formatValue(yearData.Nov) },
            { m: "Dec", v: formatValue(yearData.Dec) },
          ];
          transformedData.push({ year: yr, months, fullYear: formatValue(yearData.Full_Year) });
        });

        setData(transformedData);
      } else {
        throw new Error("No data found");
      }
    } catch (err) {
      console.error("Error processing data:", err);
      setError(err.message || "Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    processData();
  }, []);

  if (loading) return <LoadingComponent />;
  if (error) return <ErrorState error={error} onRetry={processData} />;

  const title = page?.title;
  const subtitle = page?.subtitle;
  const in_chart_content_line_1 = page?.in_chart_content_line_1;

  return (
    <div className="p-0 sm:p-0 md:p-0 mt-10 w-full text-card-foreground">
      <div className="flex flex-col gap-0 text-center mb-3 text-base sm:text-lg font-bold">
        <div className="flex flex-col gap-1 mb-1">
          {title && <HeadingComponent variant={"main"}>{title}</HeadingComponent>}
          {subtitle && <HeadingComponent variant={"sub"}>{subtitle}</HeadingComponent>}
        </div>
        {in_chart_content_line_1 && (
          <div className="mt-3">
            <span className="!border !border-1 p-3 text-xs text-white bg-blue-900 font-light">
              {in_chart_content_line_1}
            </span>
          </div>
        )}
      </div>

      {/* Desktop View */}
      <div
        className="hidden lg:grid overflow-x-auto border border-border gap-y-0"
        style={{ gridTemplateColumns: "repeat(12, 1fr) 90px" }}
      >
        {data.map((row) => (
          <React.Fragment key={row.year}>
            {row.months.map((month, idx) => (
              <div
                key={idx}
                className="relative h-[170px] flex items-center justify-center !border !border-border"
              >
                {month.m === "Jul" && (
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 text-[10px] rounded z-10 text-white bg-blue-900 text-primary-foreground">
                    {row.year}
                  </div>
                )}
                <DonutChart value={month.v} label={month.m} />
              </div>
            ))}
            <div className="flex flex-col items-center justify-center bg-muted !border !border-border">
              <DonutChart value={row.fullYear} label="Full_Year" showLabel={true} isFullYear={true} />
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Mobile & Tablet View */}
      <div className="lg:hidden overflow-x-auto">
        <div
          className="inline-grid min-w-max border border-border gap-y-4"
          style={{ gridTemplateColumns: "repeat(12, 1fr) 90px" }}
        >
          {data.map((row) => (
            <React.Fragment key={row.year}>
              {row.months.map((month, idx) => (
                <div
                  key={idx}
                  className="relative h-[150px] w-[80px] flex items-center justify-center border border-border bg-background"
                >
                  {month.m === "Jul" && (
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 text-[10px] rounded z-10 whitespace-nowrap bg-primary text-primary-foreground">
                      {row.year}
                    </div>
                  )}
                  <DonutChart value={month.v} label={month.m} />
                </div>
              ))}
              <div className="flex flex-col items-center justify-center w-[90px] bg-muted text-white bg-blue-900 !border !border-border">
                <DonutChart value={row.fullYear} label="Full_Year" showLabel={true} isFullYear={true} />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Kpi_Chart_13;